import React, { useState, useEffect, useMemo, useCallback } from 'react';

import { useQuery } from 'react-query';

import { useNavigate } from 'react-router-dom';

import LoadingSpinner from '../../components/common/LoadingSpinner';
import { getImageUrl } from '../../config/apiConfig';

import authService from '../../services/api/auth';

import { 

  Search, 

  Users, 

  BookOpen, 

  Star, 

  MapPin, 

  Filter, 

  ChevronDown, 

  ChevronLeft,

  Layers,

  Sparkles,

  Info,

  ArrowRight,

  UserCheck

} from 'lucide-react';



const TeachersList = () => {

  const navigate = useNavigate();

  // Constants
  const TEACHERS_PER_PAGE = 6;

  // State variables
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  const [selectedStage, setSelectedStage] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [sortBy, setSortBy] = useState('rating');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Debounce search term for better performance and avoiding race conditions
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // جلب بيانات الفلاتر (المراحل، الصفوف، المواد)
  const { data: stagesData } = useQuery(['stages-data', 'list'], 
    () => authService.getStages(),
    {
      retry: (failureCount, error) => {
        // Don't retry cancelled requests
        if (error?.code === 'ERR_CANCELED') return false;
        // Retry 5xx errors max 2 times
        const status = error?.response?.status;
        return [500, 502, 503, 429, 408].includes(status) && failureCount < 2;
      },
      staleTime: 300000,
      refetchOnWindowFocus: false
    }
  );

  const { data: subjectsData } = useQuery(['subjects-data', 'list'], 
    () => authService.getCategories(),
    {
      retry: (failureCount, error) => {
        // Don't retry cancelled requests
        if (error?.code === 'ERR_CANCELED') return false;
        // Retry 5xx errors max 2 times
        const status = error?.response?.status;
        return [500, 502, 503, 429, 408].includes(status) && failureCount < 2;
      },
      staleTime: 300000,
      refetchOnWindowFocus: false
    }
  );



  // Combine filters data
  const filters = React.useMemo(() => {
    return {
      stages: stagesData?.stages || [],
      subjects: subjectsData?.subjects || []
    };
  }, [stagesData, subjectsData]);

  // Extract grades from stages data since grades API doesn't filter properly
  const allGrades = React.useMemo(() => {
    // Always use grades from stages data since it contains properly nested grades
    if (selectedStage && filters?.stages?.length > 0) {
      const selectedStageData = filters.stages.find(stage => stage.id === parseInt(selectedStage));
      
      if (selectedStageData?.grades && Array.isArray(selectedStageData.grades)) {
        return selectedStageData.grades.map(grade => ({
          id: grade.id,
          name: grade.name,
          stage_id: grade.stage_id
        }));
      }
    }
    
    // Return all grades from all stages
    const allGradesList = [];
    filters?.stages?.forEach(stage => {
      if (stage.grades && Array.isArray(stage.grades)) {
        stage.grades.forEach(grade => {
          if (!allGradesList.find(g => g.id === grade.id)) {
            allGradesList.push({
              id: grade.id,
              name: grade.name,
              stage_id: grade.stage_id
            });
          }
        });
      }
    });
    
    return allGradesList;
  }, [selectedStage, filters?.stages]);

  // جلب قائمة المدرسين مع الفلاتر من السيرفر (Pagination حقيقي)
  const { data: teachersData, isLoading } = useQuery(
    ['public', 'teachers-list', selectedStage, selectedGrade, selectedSubject, sortBy, currentPage, debouncedSearch],
    ({ signal }) => {
      const params = new URLSearchParams();
      
      // Server-side search
      if (debouncedSearch) params.append('search', debouncedSearch);
      
      // Server-side filters
      if (selectedStage) params.append('stage', selectedStage);
      if (selectedGrade) params.append('grade', selectedGrade);
      if (selectedSubject) params.append('subject', selectedSubject);
      
      // Server-side sorting
      params.append('sort', sortBy);
      
      // Server-side pagination
      params.append('page', currentPage);
      params.append('limit', TEACHERS_PER_PAGE);
      
      return authService.searchTeachers(Object.fromEntries(params), signal);
    },
    {
      retry: (failureCount, error) => {
        // Don't retry on cancelled requests - this is expected when filters change
        if (error?.code === 'ERR_CANCELED') return false;
        // Retry max 1 time for network errors
        return failureCount < 1;
      },
      staleTime: 30000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      // Suppress error notifications for cancelled requests
      suspense: false,
      onError: (error) => {
        // Silently ignore cancelled requests - this is normal behavior
        // when component unmounts or when dependencies change
        if (error?.code === 'ERR_CANCELED') {
          return;
        }
        // Only log non-cancelled errors
        if (error?.message) {
          console.warn('⚠️ Teachers search error:', error.message);
        }
      }
    }
  );

  // Server-side pagination data
  const totalCount = teachersData?.total || 0;
  const totalPages = teachersData?.totalPages || 1;

  // Pagination logic
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Smart pagination window logic - only renders visible pages + neighbors
  const paginationPages = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    if (startPage > 1) pages.push(1);
    if (startPage > 2) pages.push('...');

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages - 1) pages.push('...');
    if (endPage < totalPages) pages.push(totalPages);

    return pages;
  }, [currentPage, totalPages]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedStage, selectedGrade, selectedSubject, sortBy, searchTerm]);

  // جلب بيانات المدرسين
  const groupedTeachers = React.useMemo(() => {
    const teachersToProcess = teachersData?.teachers || [];
    return teachersToProcess.map(teacher => ({
      ...teacher,
      grades: teacher.grade_names ? teacher.grade_names.split(', ').filter(Boolean) : [],
      subjects: teacher.subject_names ? teacher.subject_names.split(', ').filter(Boolean) : [],
      teacher_subject_ids: teacher.teacher_subject_ids_str ? teacher.teacher_subject_ids_str.split(',').map(id => parseInt(id)).filter(Boolean) : []
    }));
  }, [teachersData]);

  // No client-side search or pagination - use server data directly
  const currentTeachers = groupedTeachers;

  // Reset all filters - includes selectedGrade and sortBy
  const handleResetFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedStage('');
    setSelectedGrade('');
    setSelectedSubject('');
    setSortBy('rating');
    setCurrentPage(1);
  }, []);

  if (isLoading) {

    return (

      <div className="flex flex-col items-center justify-center min-h-[500px]">

        <LoadingSpinner size="large" />

        <p className="mt-4 text-gray-500 font-bold animate-pulse">جاري البحث عن نخبة المدرسين...</p>

      </div>

    );

  }



  return (

    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-10 bg-gray-50/30 min-h-screen">

      

      {/* Header Section */}

      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10 relative overflow-hidden">

        <div className="absolute top-0 left-0 w-32 h-32 bg-primary-50 rounded-br-[5rem] opacity-40"></div>

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">

            <div className="space-y-2">

                <div className="flex items-center gap-2 text-primary-600 font-bold text-sm uppercase tracking-widest">

                    <Sparkles size={20} />

                    نخبة المدرسين المتخصصين

                </div>

                <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight">ابحث عن مدرسك</h1>

                <p className="text-gray-500 max-w-xl font-medium">اكتشف أفضل الكوادر التعليمية، قارن بين المدرسين، واشترك في أقوى الكورسات التدريبية للثانوية العامة.</p>

            </div>

            <div className="hidden lg:block">

                <div className="bg-primary-50 p-6 rounded-[2rem] border border-primary-100 flex items-center gap-4">

                    <div className="w-12 h-12 bg-primary-600 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-primary-200">

                        <Users size={24} />

                    </div>

                    <div>

                        <p className="text-xs font-black text-primary-700 uppercase">إجمالي المدرسين</p>

                        <p className="text-2xl font-black text-gray-900">{totalCount || 0}</p>

                    </div>

                </div>

            </div>

        </div>

      </div>



      {/* Modern Search & Filters Toolkit */}

      <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6 overflow-visible relative z-40">

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">

          

          {/* Main Search Input */}

          <div className="relative md:col-span-6 group">

            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500 transition-colors w-5 h-5" />

            <input
              type="text"
              placeholder="ابحث باسم المدرس أو المادة العلمية..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pr-12 pl-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-medium"
            />

          </div>

          

          {/* Sort By Dropdown */}

          <div className="md:col-span-3">

            <select

              value={sortBy}

              onChange={(e) => setSortBy(e.target.value)}

              className="w-full px-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all font-bold text-gray-700 appearance-none cursor-pointer"

            >

              <option value="rating">الأعلى تقييماً</option>

              <option value="students">الأكثر إقبالاً (طلاب)</option>

              <option value="courses">الأكثر محتوى (كورسات)</option>

              <option value="name">الترتيب الأبجدي</option>

            </select>

          </div>



          {/* Action Button */}

          <div className="md:col-span-3">

            <button

              onClick={() => setShowFilters(!showFilters)}

              className={`w-full flex items-center justify-center gap-2 py-4 border rounded-2xl font-bold transition-all ${showFilters ? 'bg-primary-50 border-primary-200 text-primary-600' : 'bg-white border-gray-100 text-gray-500 hover:bg-gray-50'}`}

            >

              <Filter size={18} />

              الفلاتر

              <ChevronDown size={18} className={`transition-transform duration-300 ${showFilters ? 'rotate-180' : ''}`} />

            </button>

          </div>

        </div>



        {/* Expandable Advanced Filters */}

        {showFilters && (

          <div className="mt-6 pt-6 border-t border-gray-50 grid grid-cols-1 md:grid-cols-3 gap-4

                          animate-in fade-in slide-in-from-top duration-300

                          relative z-50 overflow-visible">

            <div className="space-y-2 relative overflow-visible">

                <label className="text-[10px] font-black text-gray-400 uppercase mr-2 tracking-widest">المرحلة الدراسية</label>

                <div className="relative overflow-visible">

                    <select

                    value={selectedStage}

                    onChange={(e) => {

                      setSelectedStage(e.target.value);

                      setSelectedGrade(''); // Reset grade when stage changes

                    }}

                    className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"

                    >

                    <option value="">جميع المراحل</option>

                    {filters?.stages?.map((stage) => (

                        <option key={stage.id} value={stage.id}>{stage.name}</option>

                    ))}

                    </select>

                </div>

            </div>



            <div className="space-y-2 relative overflow-visible">

                <label className="text-[10px] font-black text-gray-400 uppercase mr-2 tracking-widest">الصف الدراسي</label>

                <div className="relative overflow-visible">

                    <select

                    value={selectedGrade}

                    onChange={(e) => setSelectedGrade(e.target.value)}

                    className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"

                    >

                    <option value="">جميع الصفوف</option>

                    {allGrades?.map((grade) => (

                        <option key={grade.id} value={grade.id}>{grade.name}</option>

                    ))}

                    </select>

                </div>

            </div>



            <div className="space-y-2 relative overflow-visible">

                <label className="text-[10px] font-black text-gray-400 uppercase mr-2 tracking-widest">المادة العلمية</label>

                <div className="relative overflow-visible">

                    <select

                    value={selectedSubject}

                    onChange={(e) => setSelectedSubject(e.target.value)}

                    className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"

                    >

                    <option value="">جميع المواد</option>

                    {filters?.subjects?.map((subject) => (

                        <option key={subject.id} value={subject.id}>{subject.name}</option>

                    ))}

                    </select>

                </div>

            </div>

          </div>

        )}

      </div>



      {/* Results Header Info */}

      <div className="flex items-center justify-between px-2">

          <div className="flex items-center gap-2">

              <UserCheck size={20} className="text-primary-600" />

              <h2 className="text-xl font-black text-gray-900">

                  تم العثور على ({totalCount || 0}) مدرس متخصص

              </h2>

          </div>

          {searchTerm && (

              <span className="text-sm font-bold text-gray-400">البحث عن: <span className="text-primary-600">"{searchTerm}"</span></span>

          )}

      </div>



      {/* Teachers Responsive Grid */}

      <div className="space-y-12">

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">

        {currentTeachers?.length === 0 ? (

          <div className="col-span-full bg-white rounded-[3rem] p-20 text-center border border-dashed border-gray-200 shadow-sm">

            <Users size={64} className="text-gray-200 mx-auto mb-6" />

            <h3 className="text-2xl font-black text-gray-900 mb-2">لم نجد مدرسين يطابقون بحثك</h3>

            <p className="text-gray-500 max-w-sm mx-auto">نعتذر، حاول تغيير معايير الفلترة أو ابحث بكلمات أخرى للوصول إلى المدرس المطلوب.</p>

            <button 
                onClick={handleResetFilters}
                className="mt-8 bg-primary-600 text-white px-10 py-4 rounded-2xl font-black shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all flex items-center gap-2 mx-auto"
            >

                إعادة تعيين البحث

            </button>

          </div>

        ) : (

          currentTeachers?.map((teacher) => {

            return (

            <div key={teacher.teacher_id} className="group bg-white rounded-[2.5rem] shadow-sm hover:shadow-2xl transition-all duration-500 border border-gray-100 flex flex-col overflow-hidden">

              <div className="p-8 flex flex-col flex-1">

                

                {/* Profile Header */}

                <div className="flex items-start gap-5 mb-6">

                  <div className="relative flex-shrink-0">

                    {teacher.avatar ? (
                      <img
                        src={getImageUrl(teacher.avatar)}
                        alt={teacher.teacher_name}
                        className="w-20 h-20 rounded-[1.5rem] object-cover border-4 border-gray-50 shadow-sm group-hover:scale-110 transition-transform duration-500"
                        onError={(e) => { e.target.onerror = null; /* hide broken image */ e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-[1.5rem] bg-gray-100 flex items-center justify-center text-xl font-black text-gray-700 border-4 border-gray-50 shadow-sm">
                        {teacher.teacher_name?.charAt(0) || 'T'}
                      </div>
                    )}

                    <div className="absolute -bottom-1 -left-1 bg-green-500 w-5 h-5 rounded-full border-4 border-white shadow-sm" title="متاح الآن"></div>

                  </div>

                  

                  <div className="flex-1 min-w-0">

                    <h3 className="text-xl font-black text-gray-900 group-hover:text-primary-600 transition-colors line-clamp-1">{teacher.teacher_name}</h3>

                    <div className="flex items-center text-xs font-bold text-gray-400 mt-1 uppercase tracking-tighter">

                      <MapPin size={14} className="ml-1 text-primary-400" />

                      {teacher.address || 'منصة المنصة التعليمية'}

                    </div>

                    

                    <div className="mt-3 flex items-center gap-1.5">

                        <div className="flex items-center text-yellow-500">

                            <Star size={16} fill="currentColor" />

                            <span className="mr-1 text-sm font-black text-gray-900">{teacher.teacher_rating || '0'}</span>

                        </div>

                        <span className="text-[10px] text-gray-400 font-bold bg-gray-50 px-2 py-0.5 rounded">({teacher.teacher_reviews_count || 0} تقييم)</span>

                    </div>

                  </div>

                </div>



                {/* Expertise Labels */}

                <div className="space-y-2 mb-6">

                  <div className="flex flex-wrap gap-2">

                    {teacher.subjects?.map((subject, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1.5 rounded-xl text-xs font-black bg-primary-50 text-primary-700 border border-primary-100"
                        >
                          <BookOpen size={12} className="ml-1" />
                          مدرس {subject}
                        </span>
                    ))}

                  </div>

                  <div className="flex items-center gap-2 text-xs font-bold text-gray-500 mr-2">

                    <Layers size={14} className="text-gray-300" />

                    {teacher.grades?.join(' • ') || teacher.grade_name} • {teacher.stage_names}

                  </div>

                </div>



                {/* Quick Stats Dashboard */}

                <div className="grid grid-cols-3 gap-3 mb-6">

                  <div className="bg-gray-50/50 p-3 rounded-2xl text-center border border-transparent group-hover:border-blue-50 transition-colors">

                    <p className="text-lg font-black text-gray-900 leading-none">{teacher.course_count || 0}</p>

                    <p className="text-[10px] font-bold text-gray-400 mt-1 uppercase">كورس</p>

                  </div>

                  <div className="bg-gray-50/50 p-3 rounded-2xl text-center border border-transparent group-hover:border-green-50 transition-colors">

                    <p className="text-lg font-black text-gray-900 leading-none">{teacher.student_count || 0}</p>

                    <p className="text-[10px] font-bold text-gray-400 mt-1 uppercase">طالب</p>

                  </div>

                  <div className="bg-gray-50/50 p-3 rounded-2xl text-center border border-transparent group-hover:border-purple-50 transition-colors">

                    <p className="text-lg font-black text-gray-900 leading-none">{teacher.lesson_count || 0}</p>

                    <p className="text-[10px] font-bold text-gray-400 mt-1 uppercase">درس</p>

                  </div>

                </div>



                {/* Professional Bio */}

                <p className="text-sm text-gray-500 mb-8 line-clamp-2 leading-relaxed h-10 italic">

                  {teacher.bio || `خبير في مادة ${teacher.subjects?.[0] || teacher.subject_name} بخبرة تتجاوز الـ 10 سنوات في تدريس مناهج الثانوية العامة.`}

                </p>



                {/* Footer Action Button */}

                <div className="mt-auto">

                  <button

                    onClick={() => navigate(`/teachers/${teacher.teacher_id}`)}

                    className="w-full bg-primary-600 text-white px-6 py-3.5 rounded-2xl font-black text-sm hover:bg-primary-700 transition-all flex items-center justify-center gap-2 shadow-xl shadow-primary-100 active:scale-95"

                  >

                    عرض الملف الشخصي <ChevronLeft size={18} />

                  </button>

                </div>

              </div>

            </div>

            );

          })

        )}

      </div>

      </div>

      

      {/* Pagination Controls */}

      {totalPages > 1 && (

        <div className="flex items-center justify-center gap-2 py-8">

          {/* Previous Button */}

          <button

            onClick={() => handlePageChange(currentPage - 1)}

            disabled={currentPage === 1}

            className={`px-4 py-2 rounded-xl font-bold transition-all flex items-center gap-2 ${

              currentPage === 1

                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'

                : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm'

            }`}

          >

            <ChevronLeft size={18} />

            السابق

          </button>



          {/* Page Numbers */}

          <div className="flex items-center gap-1">

            {paginationPages.map((pageNum, idx) => {

              if (pageNum === '...') {

                return (

                  <span

                    key={`ellipsis-${idx}`}

                    className="px-2 py-1 text-gray-400 font-bold"

                  >

                    ...

                  </span>

                );

              }



              return (

                <button

                  key={pageNum}

                  onClick={() => handlePageChange(pageNum)}

                  className={`w-10 h-10 rounded-xl font-black transition-all ${

                    currentPage === pageNum

                      ? 'bg-primary-600 text-white shadow-lg shadow-primary-100'

                      : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm'

                  }`}

                >

                  {pageNum}

                </button>

              );

            })}

          </div>



          {/* Next Button */}

          <button

            onClick={() => handlePageChange(currentPage + 1)}

            disabled={currentPage === totalPages}

            className={`px-4 py-2 rounded-xl font-bold transition-all flex items-center gap-2 ${

              currentPage === totalPages

                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'

                : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm'

            }`}

          >

            التالي

            <ChevronLeft size={18} className="rotate-180" />

          </button>

        </div>

      )}



      {/* Page Info */}

      <div className="text-center text-sm text-gray-500 font-bold pb-8">

        عرض {currentTeachers?.length || 0} مدرس من {totalCount} مدرس

        {totalPages > 1 && ` • الصفحة ${currentPage} من ${totalPages}`}

      </div>

      

      <div className="bg-gray-900 rounded-[3rem] p-10 text-white flex flex-col md:flex-row items-center justify-between gap-8 shadow-2xl relative overflow-hidden group">

        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-600/20 rounded-full blur-3xl -z-0"></div>

        <div className="relative z-10 text-center md:text-right space-y-2">

            <h3 className="text-2xl font-black flex items-center gap-2 justify-center md:justify-start leading-tight">

                <Info className="text-primary-500" /> لم تجد المدرس المطلوب؟

            </h3>

            <p className="text-gray-400 font-medium">نحن نقوم بإضافة نخبة جديدة من المدرسين أسبوعياً، تواصل معنا إذا كنت تبحث عن تخصص معين.</p>

        </div>

        <button 

            onClick={() => navigate('/contact')}

            className="relative z-10 bg-white text-gray-900 px-10 py-4 rounded-2xl font-black flex items-center gap-2 shadow-xl hover:bg-primary-50 transition-colors transform active:scale-95"

        >

            تواصل مع الدعم الفني <ArrowRight size={20} />

        </button>

      </div>

      </div>

  );

};



export default TeachersList;